import * as DatasetAPI from "api/DatasetAPI";
import * as RecipeAPI from "api/RecipeAPI";

import { delay } from "utils/promise";

import { isEnabled } from "utils/featureFlags";

import { updateIsRecipesLoading } from "containers/App/reducer";

import { transformUpdateDataset } from "containers/Home/reducer";

import { getSelectedAccountId } from "containers/Accounts/reducer";

const RESET = "recipe/RESET";
const UPDATE_KEY = "recipe/UPDATE_KEY";
const UPDATE_SUB_KEY = "recipe/UPDATE_SUB_KEY";
const UPDATE_SUB_KEY_AND_MERGE = "recipe/UPDATE_SUB_KEY_AND_MERGE";

const CURRENT_SOLUTION_VERSION = 1.0;

export const RecordStatus = {
  NOT_DELETED: "NOT_DELETED",
  DELETED: "DELETED"
};

export const RecipeType = {
  Tray: "tray"
};

export const RecipeStatus = {
  Created: "Created",
  Configuring: "Configuring",
  Deleting: "Deleting"
};

export const SolutionVendor = {
  Microsoft: "microsoft",
  Salesforce: "salesforce",
  SAP: "sap",
  Marketo: "marketo",
  Oracle: "oracle"
};

export const RecipeVersionInstanceStatus = {
  Initialized: "initialized",
  Running: "running",
  Complete: "complete",
  Error: "error"
};

export const ListStatus = {
  NotLoaded: "NotLoaded",
  Loading: "Loading",
  Loaded: "Loaded"
};

export const RecipeCreationStatus = {
  NotCreating: "NotCreating",
  Creating: "Creating"
};

const initialState = {
  recipes: {},
  recipeVersions: {},
  recipeVersionInstances: {},
  blueprintRecipeVersionInstanceLink: {},
  deploymentRecipeVersionInstanceLink: {},
  solutions: {},
  solutionListStatus: ListStatus.NotLoaded,
  recipeListStatus: ListStatus.NotLoaded,
  recipeVersionListStatus: ListStatus.NotLoaded,
  recipeCreationStatus: {},
  recipeStatus: {},
  createRecipeModalIsOpen: false
};

function globalReducer(state = initialState, action) {
  switch (action.type) {
    case RESET:
      return initialState;
    case UPDATE_KEY:
      return {
        ...state,
        [action.key]: action.value
      };
    case UPDATE_SUB_KEY:
      return {
        ...state,
        [action.key]: {
          ...state[action.key],
          [action.subKey]: action.value
        }
      };
    case UPDATE_SUB_KEY_AND_MERGE:
      return {
        ...state,
        [action.key]: {
          ...state[action.key],
          [action.subKey]: {
            ...(state[action.key] || {})[action.subKey],
            ...action.value
          }
        }
      };
    default:
      return state;
  }
}

export default globalReducer;

export function getIsRecipesEnabled() {
  return isEnabled("recipes");
}

/* Selectors */
function getBase(state) {
  return state.recipe;
}

function getRecipesObject(state) {
  return getBase(state).recipes;
}

function getRecipesVersionsObject(state) {
  return getBase(state).recipeVersions;
}

function getRecipesVersionInstancesObject(state) {
  return getBase(state).recipeVersionInstances;
}

function getSolutionsObject(state) {
  return getBase(state).solutions;
}

export function getRecipes(state) {
  const recipes = getRecipesObject(state);

  return Object.keys(recipes)
    .map((key) => recipes[key])
    .filter((recipe) => recipe.deleted !== RecordStatus.DELETED)
    .sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate));
}

export function getRecipeVersions(state) {
  const recipeVersions = getRecipesVersionsObject(state);

  return Object.keys(recipeVersions)
    .map((key) => recipeVersions[key])
    .filter((recipeVersion) => recipeVersion.deleted !== RecordStatus.DELETED)
    .sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate));
}

export function getRecipeIds(state) {
  return getRecipes(state).map((recipe) => recipe.id);
}

export function getRecipeIdsBySolutionId(state, solutionId) {
  return getRecipes(state)
    .filter((recipe) => recipe.traySolution && recipe.traySolution.id == solutionId)
    .map((recipe) => recipe.id);
}

export function getRecipeIdsBySolutionType(state, solutionType) {
  return getRecipes(state)
    .filter((recipe) => recipeMatchesSolutionType(recipe, solutionType))
    .map((recipe) => recipe.id);
}

export function getRecipeVersionIdsBySolutionType(state, solutionType) {
  return getRecipeVersions(state)
    .filter((recipeVersion) => recipeMatchesSolutionType(getRecipe(state, recipeVersion.recipeId), solutionType))
    .map((recipe) => recipe.id);
}

function recipeMatchesSolutionType(recipe, solutionType) {
  return recipe && recipe.traySolution && recipe.traySolution.type == solutionType;
}

export function getRecipe(state, id) {
  return getRecipesObject(state)[id];
}

export function getRecipeName(state, id) {
  return (getRecipe(state, id) || {}).name;
}

export function getRecipeVersion(state, id) {
  return getRecipesVersionsObject(state)[id];
}

export function getRecipeVersionListStatus(state) {
  return getBase(state).recipeVersionListStatus;
}

export function getRecipeVersionListIsLoading(state) {
  return getRecipeVersionListStatus(state) == RecipeVersionListStatus.Loading;
}

export function getRecipeVersionInstance(state, id) {
  return getRecipesVersionInstancesObject(state)[id];
}

export function getRecipeVersionInstanceStatus(state, id) {
  const recipeVersionInstance = getRecipeVersionInstance(state, id) || {};

  return recipeVersionInstance.status;
}

export function getRecipeStatus(state, id) {
  return getBase(state).recipeStatus[id];
}

export function isRecipeUpdating(state, id) {
  const status = getRecipeStatus(state, id);

  return status == RecipeStatus.Configuring || status == RecipeStatus.Deleting;
}

export function getImportVendor(state, id) {
  const recipe = getRecipe(state, id);

  return recipe && recipe.traySolution && recipe.traySolution.vendor ? recipe.traySolution.vendor : undefined;
}

export function getTrayRecipe(state, id) {
  const recipe = getRecipe(state, id) || {};

  if (recipe.type != RecipeType.Tray) {
    return undefined;
  }

  return recipe;
}

export function getSolutions(state) {
  const solutions = getSolutionsObject(state);

  return Object.keys(solutions).map((key) => solutions[key]);
}

export function getSolution(state, id) {
  return getSolutionsObject(state)[id];
}

export function getSolutionIds(state) {
  return getSolutions(state)
    .filter((solution) => solution.version >= CURRENT_SOLUTION_VERSION)
    .map((solution) => solution.id);
}

export function getSolutionListStatus(state) {
  return getBase(state).solutionListStatus;
}

export function getSolutionListIsLoading(state) {
  return getSolutionListStatus(state) == ListStatus.Loading;
}

export function getRecipeCreationStatusObject(state, id) {
  return getBase(state).recipeCreationStatus;
}

export function getRecipeCreationStatus(state, id) {
  return getRecipeCreationStatusObject(state)[id];
}

export function isRecipeCreating(state, id) {
  return getRecipeCreationStatusObject(state)[id] == RecipeCreationStatus.Creating;
}

export function areNoRecipesBeingCreated(state) {
  const allStatuses = getRecipeCreationStatusObject(state);

  for (let status of Object.values(allStatuses)) {
    if (status == RecipeCreationStatus.Creating) {
      return false;
    }
  }

  return true;
}

export function getCreateRecipeModalIsOpen(state) {
  return getBase(state).createRecipeModalIsOpen;
}

export function getTraySolutionConfigurations(state, id) {
  const recipe = getTrayRecipe(state, id) || {};

  return recipe.traySolutionConfigurations;
}

export function getRecipeVersionInstanceStatusByBlueprintId(state, blueprintId) {
  const recipeInstanceId = getRecipeVersionInstanceIdByBlueprintId(state, blueprintId);

  return getRecipeVersionInstanceStatus(state, recipeInstanceId);
}

export function getRecipeVersionInstanceIdByBlueprintId(state, blueprintId) {
  return getBase(state).blueprintRecipeVersionInstanceLink[blueprintId];
}

export function getRecipeVersionInstancesByDeploymentId(state, deploymentId) {
  const instanceIds = getBase(state).deploymentRecipeVersionInstanceLink[deploymentId] || [];

  return instanceIds
    .map((id) => getRecipeVersionInstance(state, id))
    .filter((instance) => instance !== undefined && instance.deleted !== RecordStatus.DELETED)
    .sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate));
}

export function hasAnyDeployedRecipes(state, deploymentId) {
  const deployedInstances = getRecipeVersionInstancesByDeploymentId(state, deploymentId);

  return deployedInstances.length > 0;
}

/* Actions */
export function setRecipe(id, recipe) {
  return { type: UPDATE_SUB_KEY_AND_MERGE, key: "recipes", subKey: id, value: recipe };
}

export function setRecipeVersion(id, recipeVersion) {
  return { type: UPDATE_SUB_KEY_AND_MERGE, key: "recipeVersions", subKey: id, value: recipeVersion };
}

export function setRecipeVersionListStatus(status) {
  return { type: UPDATE_KEY, key: "recipeVersionListStatus", value: status };
}

export function setRecipeVersionInstance(id, recipeVersionInstance) {
  return {
    type: UPDATE_SUB_KEY_AND_MERGE,
    key: "recipeVersionInstances",
    subKey: id,
    value: recipeVersionInstance
  };
}

export function setRecipeVersionInstanceStatus(id, status) {
  return { type: UPDATE_SUB_KEY, key: "recipeVersionInstancesStatus", subKey: id, value: status };
}

export function setSolution(id, solution) {
  return { type: UPDATE_SUB_KEY_AND_MERGE, key: "solutions", subKey: id, value: solution };
}

export function setSolutionListStatus(status) {
  return { type: UPDATE_KEY, key: "solutionListStatus", value: status };
}

export function setRecipeCreationStatus(id, status) {
  return { type: UPDATE_SUB_KEY, key: "recipeCreationStatus", subKey: id, value: status };
}

export function setRecipeStatus(id, status) {
  return { type: UPDATE_SUB_KEY, key: "recipeStatus", subKey: id, value: status };
}

export function setCreateRecipeModalIsOpen(isOpen) {
  return { type: UPDATE_KEY, key: "createRecipeModalIsOpen", value: isOpen };
}

export function linkBlueprintToRecipeVersionInstance(blueprintId, recipeVersionInstanceId) {
  return {
    type: UPDATE_SUB_KEY,
    key: "blueprintRecipeVersionInstanceLink",
    subKey: blueprintId,
    value: recipeVersionInstanceId
  };
}

export function linkDeploymentToRecipeVersionInstances(deploymentId, recipeVersionInstanceIds) {
  return {
    type: UPDATE_SUB_KEY,
    key: "deploymentRecipeVersionInstanceLink",
    subKey: deploymentId,
    value: recipeVersionInstanceIds
  };
}

export function reset() {
  return { type: RESET };
}

export function resetRecipes() {
  return { type: UPDATE_KEY, key: "recipes", value: {} };
}

export function resetSolutions() {
  return { type: UPDATE_KEY, key: "solutions", value: {} };
}

/* Action Creators */
export function fetchRecipes() {
  return (dispatch, getState) => {
    dispatch(updateIsRecipesLoading(true));

    return dispatch(fetchAll()).then(({ recipes }) => {
      dispatch(updateIsRecipesLoading(false));

      return Promise.resolve(recipes);
    });
  };
}

export function fetchAllRecipes() {
  return (dispatch, getState) => {
    return RecipeAPI.getRecipes().then((recipes) => {
      return Promise.all(recipes.map(processRecipeResponse).map(dispatch));
    });
  };
}

export function fetchAllRecipeVersions() {
  return (dispatch, getState) => {
    return RecipeAPI.getRecipeVersions().then((recipeVersions) => {
      return Promise.all(recipeVersions.map(processRecipeVersionResponse).map(dispatch));
    });
  };
}

export function fetchAllRecipeVersionInstances() {
  return (dispatch, getState) => {
    return RecipeAPI.getRecipeVersionInstances().then((response) => {
      return Promise.all(response.map(processRecipeVersionInstanceResponse).map(dispatch));
    });
  };
}

export function fetchAll() {
  return (dispatch, getState) => {
    return Promise.all([
      dispatch(fetchAllRecipes()),
      dispatch(fetchAllRecipeVersions()),
      dispatch(fetchAllRecipeVersionInstances())
    ]).then((all) =>
      Promise.resolve({
        recipes: all[0],
        recipeVersions: all[1],
        recipeVersionInstances: all[2]
      })
    );
  };
}

export function createRecipeFromSolution(solutionId) {
  return (dispatch, getState) => {
    dispatch(setRecipeCreationStatus(solutionId, RecipeCreationStatus.Creating));

    return RecipeAPI.createRecipeFromTraySolution(solutionId)
      .then((response) => dispatch(processRecipeResponse(response)))
      .finally((recipe) => {
        dispatch(setRecipeCreationStatus(solutionId, RecipeCreationStatus.NotCreating));
        return Promise.resolve(recipe);
      });
  };
}

export function loadRecipeIfNotPresent(recipeId, forceUpdate = false) {
  return (dispatch, getState) => {
    const recipe = getRecipe(getState(), recipeId);

    if (recipe === undefined || forceUpdate) {
      return RecipeAPI.getRecipe(recipeId).then((response) => dispatch(processRecipeResponse(response)));
    }
  };
}

export function fetchLatestRecipe(recipeId) {
  return (dispatch, getState) => {
    return dispatch(loadRecipeIfNotPresent(recipeId, true));
  };
}

export function deleteRecipe(recipeId) {
  return (dispatch, getState) => {
    dispatch(setRecipeStatus(recipeId, RecipeStatus.Deleting));

    return RecipeAPI.deleteRecipe(recipeId).then((response) => dispatch(processRecipeResponse(response)));
  };
}

export function configureRecipe(recipeId) {
  return (dispatch, getState) => {
    dispatch(setRecipeStatus(recipeId, RecipeStatus.Configuring));

    return dispatch(fetchLatestRecipeVersionByRecipeId(recipeId))
      .then((recipeVersion) => dispatch(configureRecipeVersion(recipeVersion.id)))
      .finally(() => dispatch(setRecipeStatus(recipeId, RecipeStatus.Created)));
  };
}

export function createRecipeVersion(recipeId) {
  return (dispatch, getState) => {
    return RecipeAPI.createRecipeVersion(recipeId).then((response) => dispatch(processRecipeVersionResponse(response)));
  };
}

export function loadRecipeVersionIfNotPresent(recipeVersionId, forceUpdate = false) {
  return (dispatch, getState) => {
    const recipeVersion = getRecipeVersion(getState(), recipeVersionId);

    if (recipeVersion === undefined || forceUpdate) {
      return RecipeAPI.getRecipeVersion(recipeVersionId).then((response) =>
        dispatch(processRecipeVersionResponse(response))
      );
    }
  };
}

export function fetchLatestRecipeVersion(recipeVersionId) {
  return (dispatch, getState) => {
    return dispatch(loadRecipeVersionIfNotPresent(recipeVersionId, true));
  };
}

export function fetchLatestRecipeVersionByRecipeId(recipeId) {
  return (dispatch, getState) => {
    return RecipeAPI.getRecipeVersionsByRecipeId(recipeId)
      .then((response) => Promise.all(response.map(processRecipeVersionResponse).map(dispatch)))
      .then((recipeVersions) => {
        if (recipeVersions.length > 0) {
          return Promise.resolve(recipeVersions[0]);
        } else {
          return dispatch(createRecipeVersion(recipeId));
        }
      });
  };
}

export function deleteRecipeVersion(recipeVersionId) {
  return (dispatch, getState) => {
    return RecipeAPI.deleteRecipeVersion(recipeVersionId).then((response) =>
      dispatch(processRecipeVersionResponse(response))
    );
  };
}

export function createRecipeVersionInstance(recipeVersionId) {
  return (dispatch, getState) => {
    const accountId = getSelectedAccountId(getState());

    return dispatch(configureRecipeVersionIfNotConfigured(recipeVersionId))
      .then((recipeVersion) => RecipeAPI.createRecipeVersionInstance(recipeVersion.id, accountId))
      .then((response) => dispatch(processRecipeVersionInstanceResponse(response)));
  };
}

export function loadRecipeVersionInstanceIfNotPresent(recipeVersionInstanceId, forceUpdate = false) {
  return (dispatch, getState) => {
    const recipeVersionInstance = getRecipeVersionInstance(getState(), recipeVersionInstanceId);

    if (recipeVersionInstance === undefined || forceUpdate) {
      return RecipeAPI.getRecipeVersionInstance(recipeVersionInstanceId).then((response) =>
        dispatch(processRecipeVersionInstanceResponse(response))
      );
    }
  };
}

export function fetchLatestRecipeVersionInstance(recipeVersionInstanceId) {
  return (dispatch, getState) => {
    return dispatch(loadRecipeVersionInstanceIfNotPresent(recipeVersionInstanceId, true));
  };
}

export function fetchLatestRecipeVersionInstancesByDeploymentId(deploymentId) {
  return (dispatch, getState) => {
    return RecipeAPI.getRecipeVersionInstancesByDeploymentId(deploymentId).then((response) =>
      Promise.all(response.map(processRecipeVersionInstanceResponse).map(dispatch))
    );
  };
}

export function deleteRecipeVersionInstance(recipeVersionInstanceId) {
  return (dispatch, getState) => {
    return RecipeAPI.deleteRecipeVersionInstance(recipeVersionInstanceId).then((response) =>
      dispatch(processRecipeVersionInstanceResponse(response))
    );
  };
}

export function handleReset() {
  return (dispatch, getState) => {
    dispatch(resetRecipes());
    dispatch(setSolutionListStatus(ListStatus.NotLoaded));
    dispatch(resetSolutions());
  };
}

export function fetchSolutions() {
  return (dispatch, getState) => {
    const status = getSolutionListStatus(getState());

    if (status == ListStatus.NotLoaded) {
      dispatch(setSolutionListStatus(ListStatus.Loading));

      return RecipeAPI.getTraySolutions().then((recipes) => {
        dispatch(setSolutionListStatus(ListStatus.Loaded));

        return Promise.all(recipes.map(processSolutionResponse).map(dispatch));
      });
    }
  };
}

export function processRecipeResponse(response) {
  return (dispatch, getState) => {
    const recipe = formatRecipeResponse(response);

    dispatch(setRecipe(recipe.id, recipe));

    return Promise.resolve(recipe);
  };
}

export function processRecipeVersionResponse(response) {
  return (dispatch, getState) => {
    const recipeVersion = formatRecipeVersionResponse(response);

    dispatch(setRecipeVersion(recipeVersion.id, recipeVersion));

    return Promise.resolve(recipeVersion);
  };
}

export function processRecipeVersionInstanceResponse(response) {
  return (dispatch, getState) => {
    const recipeVersionInstance = formatRecipeVersionInstanceResponse(response);

    if (recipeVersionInstance.deploymentId !== undefined) {
      const deploymentId = recipeVersionInstance.deploymentId;

      const instanceIds = [
        recipeVersionInstance.id,
        ...getRecipeVersionInstancesByDeploymentId(getState(), deploymentId).map((x) => x.id)
      ];

      dispatch(linkDeploymentToRecipeVersionInstances(deploymentId, [...new Set(instanceIds)])); // remove duplicates
    }

    dispatch(setRecipeVersionInstance(recipeVersionInstance.id, recipeVersionInstance));

    return Promise.resolve(recipeVersionInstance);
  };
}

export function processSolutionResponse(response) {
  return (dispatch, getState) => {
    const solution = formatSolutionResponse(response);

    dispatch(setSolution(solution.id, solution));

    return Promise.resolve(solution);
  };
}

export function configureRecipeVersion(recipeVersionId) {
  return (dispatch, getState) => {
    return dispatch(configureRecipeVersionIfNotConfigured(recipeVersionId, true));
  };
}

export function configureRecipeVersionIfNotConfigured(recipeVersionId, forceUpdate = false) {
  return (dispatch, getState) => {
    return RecipeAPI.getRecipeVersionConfig(recipeVersionId).then((response) => {
      const recipeVersionConfig = processRecipeVersionConfigResponse(response);

      if (recipeVersionConfig !== undefined && (!recipeVersionConfig.isConfigured || forceUpdate)) {
        return openRecipeVersionConfiguration(recipeVersionConfig).then(() =>
          dispatch(fetchLatestRecipeVersion(recipeVersionId))
        );
      }

      return Promise.resolve(getRecipeVersion(getState(), recipeVersionId));
    });
  };
}

export function executeRecipeImport(recipeId, onComplete) {
  return (dispatch, getState) => {
    return dispatch(fetchLatestRecipeVersionByRecipeId(recipeId)).then((recipeVersion) =>
      dispatch(
        executeRecipeVersionImport(recipeVersion.id, () => {
          if (onComplete) {
            onComplete();
          }
        })
      )
    );
  };
}

export function executeRecipeVersionImport(recipeVersionId, onComplete) {
  return (dispatch, getState) => {
    return dispatch(createRecipeVersionInstance(recipeVersionId)).then((recipeVersionInstance) => {
      dispatch(_awaitRecipeVersionImportCompletion(recipeVersionInstance.id))
        .then((completedInstance) => dispatch(_processCompletedImport(completedInstance)))
        .catch((errorInstance) => Promise.reject(`${errorInstance.name} - Failed`))
        .finally(() => {
          dispatch(deleteRecipeVersionInstance(recipeVersionInstance.id));

          if (onComplete) {
            onComplete();
          }
        });

      return Promise.resolve(recipeVersionInstance);
    });
  };
}

function _processCompletedImport(recipeVersionInstance) {
  return (dispatch, getState) => {
    return DatasetAPI.finalizeDataset({
      url: recipeVersionInstance.dataImportUrl,
      name: recipeVersionInstance.name,
      account_id: recipeVersionInstance.accountId
    }).then((response) => dispatch(transformUpdateDataset(response.id, response)));
  };
}

function _awaitRecipeVersionImportCompletion(recipeVersionInstanceId) {
  return (dispatch, getState) => {
    return dispatch(fetchLatestRecipeVersionInstance(recipeVersionInstanceId)).then((recipeVersionInstance) => {
      if (recipeVersionInstance.status == RecipeVersionInstanceStatus.Running) {
        return delay(2000).then(() => dispatch(_awaitRecipeVersionImportCompletion(recipeVersionInstanceId)));
      } else if (recipeVersionInstance.status == RecipeVersionInstanceStatus.Error) {
        return Promise.reject(recipeVersionInstance);
      } else {
        return Promise.resolve(recipeVersionInstance);
      }
    });
  };
}

export function deployRecipe(recipeId, deploymentId) {
  return (dispatch, getState) => {
    return dispatch(fetchLatestRecipeVersionByRecipeId(recipeId)).then((recipeVersion) =>
      dispatch(deployRecipeVersion(recipeVersion.id, deploymentId))
    );
  };
}

export function deployRecipeVersion(recipeVersionId, deploymentId) {
  return (dispatch, getState) => {
    return dispatch(fetchLatestRecipeVersionInstancesByDeploymentId(deploymentId)).then((recipeVersionInstances) => {
      if (recipeVersionInstances.length > 0) {
        return Promise.resolve(recipeVersionInstances[0]);
      }

      return dispatch(configureRecipeVersionIfNotConfigured(recipeVersionId))
        .then((recipeVersion) => RecipeAPI.deployRecipeVersion(recipeVersion.id, deploymentId))
        .then((response) => dispatch(processRecipeVersionInstanceResponse(response)));
    });
  };
}

export function terminateRecipeDeployment(deploymentId) {
  return (dispatch, getState) => {
    return dispatch(fetchLatestRecipeVersionInstancesByDeploymentId(deploymentId)).then((recipeVersionInstances) =>
      Promise.all(
        recipeVersionInstances
          .map((instance) => instance.id)
          .map(deleteRecipeVersionInstance)
          .map(dispatch)
      )
    );
  };
}

/* Utils */
function formatSolutionResponse(response) {
  const solution = {
    id: response.id,
    title: response.title,
    description: response.description,
    type: response.type.toLowerCase(),
    vendor: response.vendor.toLowerCase(),
    version: response.version
  };

  return solution;
}

function formatRecipeResponse(response) {
  const recipe = {
    tenantId: response.tenant_id,
    id: response.id,
    name: response.name,
    type: response.type.toLowerCase(),
    accountId: response.account_id,
    deleted: response.deleted,
    createdBy: response.created_by,
    createdDate: response.created_date,
    updatedDate: response.updated_date
  };

  if (response.tray_solution) {
    recipe.traySolution = formatSolutionResponse(response.tray_solution);
  }

  return recipe;
}

function formatRecipeVersionResponse(response) {
  const recipe = {
    tenantId: response.tenant_id,
    id: response.id,
    accountId: response.account_id,
    name: response.name,
    type: response.type.toLowerCase(),
    recipeId: response.recipe_id,
    prototypeTraySolutionInstanceId: response.prototype_tray_solution_instance_id,
    deleted: response.deleted,
    createdBy: response.created_by,
    createdDate: response.created_date,
    updatedDate: response.updated_date
  };

  return recipe;
}

function processRecipeVersionConfigResponse(response) {
  return {
    configurationWizardUrl: response.configuration_wizard_url,
    isConfigured: response.is_configured
  };
}

function formatRecipeVersionInstanceResponse(response) {
  const recipeInstance = {
    tenantId: response.tenant_id,
    id: response.id,
    name: response.name,
    type: response.type.toLowerCase(),
    accountId: response.account_id,
    status: response.status.toLowerCase(),
    recipeVersionId: response.recipe_version_id,
    traySolutionInstanceId: response.tray_solution_instance_id,
    dataImportUrl: response.data_import_url,
    deploymentId: response.deployment_id,
    deleted: response.deleted,
    createdBy: response.created_by,
    createdDate: response.created_date,
    updatedDate: response.updated_date
  };

  return recipeInstance;
}

function openRecipeVersionConfiguration(recipeVersionConfig) {
  return new Promise((resolve, reject) => {
    // Must open window from user interaction code otherwise it is likely
    // to be blocked by a popup blocker:
    const configWindow = window.open(undefined, "_blank", "width=500,height=500,scrollbars=no");

    // Listen to popup messages
    const onmessage = (e) => {
      if (e.data.type === "tray.configPopup.error") {
        configWindow.close();
        reject(e.data.err);
      }
      if (e.data.type === "tray.configPopup.cancel") {
        configWindow.close();
        resolve(recipeVersionConfig);
      }
      if (e.data.type === "tray.configPopup.finish") {
        configWindow.close();
        resolve(recipeVersionConfig);
      }
    };

    window.addEventListener("message", onmessage);

    // Check if popup window has been closed before finishing the configuration.
    // We use a polling function due to the fact that some browsers may not
    // display prompts created in the beforeunload event handler.
    const CHECK_TIMEOUT = 1000;
    const checkWindow = () => {
      if (configWindow.closed) {
        window.removeEventListener("message", onmessage);
        resolve(recipeVersionConfig);
      } else {
        setTimeout(checkWindow, CHECK_TIMEOUT);
      }
    };

    checkWindow();

    configWindow.location = recipeVersionConfig.configurationWizardUrl;
  });
}
